import { createContext, useCallback, useContext } from 'react';

import { useDispatch } from 'store';
import { aiChatThreadDeleted } from 'store/reducers/aiChat';
import { clearResumeRecord, readResumeRecord } from './aiChatResume';
import { AiChatSendOptions, AiChatSurface, useAiChatController } from './hooks/useAiChatController';
import { useUiContextRequestHandler } from './hooks/useUiContextRequestHandler';
import { PageContext, usePageContext } from './floating/usePageContext';

const API_BASE_URL = import.meta.env.VITE_API_URL;

type AiChatController = ReturnType<typeof useAiChatController>;

/** Which of the two sessions to render — not necessarily the surface rendering it. */
export type AiChatSessionKey = 'page' | 'floating';

interface AiChatSessions {
    page: AiChatController;
    floating: AiChatController;
    pageContext: PageContext | null;
    deleteThread: (threadId: number, surface: AiChatSurface) => Promise<void>;
}

const AiChatSessionContext = createContext<AiChatSessions | null>(null);

/**
 * Owns the two live chat sessions, mounted with the layout rather than with a route so neither dies
 * on navigation. The full `/ai-chat` page renders one and the floating panel the other, which is
 * what lets the two hold different conversations at the same time.
 *
 * Outliving the route is also what makes the mid-stream handoff work: following an entity link out
 * of `/ai-chat` leaves the page session streaming, and the floating panel borrows it rather than
 * reloading the thread (see `floatingSessionKey` in the store).
 *
 * Deliberately ungated: both sessions are inert until something sends a message, and gating them on
 * the feature flag would let the gate disagree with a consumer's own gate for one render.
 */
export const AiChatSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const pageContext = usePageContext();
    const { handleDataPart } = useUiContextRequestHandler();
    // Both carry the page identity: a session is attached to whichever route the user is on when
    // they send, not to the surface that created it.
    const page = useAiChatController({
        apiBaseUrl: API_BASE_URL,
        surface: 'page',
        pageContext,
        dataPartHandler: handleDataPart,
    });
    const floating = useAiChatController({
        apiBaseUrl: API_BASE_URL,
        surface: 'floating',
        pageContext,
        dataPartHandler: handleDataPart,
    });
    const dispatch = useDispatch();

    // Deleting is the one action that reaches past the session it was invoked from: a thread open
    // in both has to go from both, and `/ai-chat` has a URL naming it that only it can clean up.
    const deleteThread = useCallback(
        async (threadId: number, surface: AiChatSurface) => {
            const acting = surface === 'page' ? page : floating;
            const other = surface === 'page' ? floating : page;

            await acting.deleteThread(threadId, surface);

            if (other.threadId === threadId) {
                other.newChat();
            }

            // A live session pointing at the thread clears its record via newChat above, but a
            // record can outlive its session (reload, closed panel) — left behind, a later open
            // would try to resume a conversation that no longer exists.
            (['page', 'floating'] as const).forEach((recordSurface) => {
                if (readResumeRecord(recordSurface)?.threadId === threadId) {
                    clearResumeRecord(recordSurface);
                }
            });

            dispatch(aiChatThreadDeleted(threadId));
        },
        [dispatch, floating, page]
    );

    return (
        <AiChatSessionContext.Provider value={{ page, floating, pageContext, deleteThread }}>
            {children}
        </AiChatSessionContext.Provider>
    );
};

const useAiChatSessions = (): AiChatSessions => {
    const sessions = useContext(AiChatSessionContext);

    if (!sessions) {
        throw new Error('useAiChatSessions must be used inside AiChatSessionProvider');
    }

    return sessions;
};

/** The threads either session is answering now, so a chat answered in the panel reads as writing on the page too. */
export const useAiChatStreamingThreadIds = (): number[] => {
    const { page, floating } = useAiChatSessions();

    return [page, floating].flatMap(({ isStreaming, threadId }) => (isStreaming && threadId != null ? [threadId] : []));
};

/**
 * A session with its analytics bound to the surface rendering it. The two differ while the floating
 * panel is borrowing the page session — the user is chatting in the panel, so that is the surface
 * the events belong to.
 */
export const useAiChatSurface = (surface: AiChatSurface, sessionKey: AiChatSessionKey = surface) => {
    const sessions = useAiChatSessions();
    const controller = sessions[sessionKey];

    return {
        pageContext: sessions.pageContext,
        controller: {
            ...controller,
            send: (text: string, options?: AiChatSendOptions) => controller.send(text, surface, options),
            stopGeneration: () => controller.stopGeneration(surface),
            retryLastMessage: () => controller.retryLastMessage(surface),
            deleteThread: (threadId: number) => sessions.deleteThread(threadId, surface),
        },
    };
};

export type AiChatSurfaceController = ReturnType<typeof useAiChatSurface>['controller'];
